/**
 * "Ask [agent] for these N" on the items list's bulk bar: which agents can be
 * asked about the chosen rows, who will decide what each proposes, and the
 * act of asking.
 *
 * Built on what exists: the workspace's template agents (useTemplateAgents),
 * whether an AI control may be drawn (useAiControl), each value's AI setting
 * (valueAiSettingsStore), the agent's reviewer rule (agentReviewerAllowed), and
 * the run store every "Run now" uses (useAgentRunStore.startRun → agent_run_logs,
 * then the agent's own batch runner, templateBatchRunnerFor). The run is the
 * browser lane, under the asker's own view, with the asker recorded as the
 * person who ran it (R19, R30). Nothing here writes a decision mode.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTemplateAgents } from './useTemplateAgents';
import { useWorkspaceMembers } from './useWorkspaceMembers';
import { useAiControl } from '../lib/ai/aiAvailability';
import { isWorkspaceEntitledToAi } from '../lib/ai/aiAssistSettings';
import { agentReviewerAllowed } from '../lib/agentRuntimeService';
import { templateBatchRunnerFor } from '../lib/agentTemplateRunners';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import { planBulkAsk, type BulkAskPlan, type BulkAskRunContext } from '../lib/bulkAgentAsk';
import type { BatchStats } from '../lib/agentRunService';
import { useAgentRunStore } from '../stores/prioritization/agentRunStore';
import { useAttributesStore } from '../stores/prioritization/attributesStore';
import { useUserStore } from '../stores/prioritization/userStore';
import { useValueAiSettingsStore } from '../stores/prioritization/valueAiSettingsStore';

export interface BulkAskRunning {
  agentType: string;
  name: string;
  done: number;
  total: number;
  /** What done and total count: "items" or "estimates" (one per item and value). */
  unit: 'items' | 'estimates';
}

export interface BulkAskOutcome {
  name: string;
  status: 'completed' | 'failed' | 'cancelled' | 'idle' | 'running';
  stats: BatchStats | null;
  error: string | null;
  /** How many rows were chosen and what the agent does with them, for the closing line. */
  run: BulkAskRunContext;
}

const ADMIN_MEMBERSHIP_ROLES = new Set(['admin', 'owner']);

/**
 * @param itemIds the chosen rows
 * @param active  whether the bar is up (two or more chosen); the lookups wait until it is
 */
export function useBulkAgentAsk(itemIds: readonly string[], active: boolean) {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const isAdmin = useUserStore((s) => s.can('admin.settings'));
  const mayAcceptValues = useUserStore((s) => s.can('values.accept'));
  // The one draw rule for every AI control: asked only while the bar is up.
  const { drawable: aiDrawable, needsKey: aiNeedsKey } = useAiControl(active);
  const agents = useTemplateAgents();

  const valueSettings = useValueAiSettingsStore((s) => s.settings);
  const loadValueAi = useValueAiSettingsStore((s) => s.load);
  useEffect(() => {
    if (active && workspaceId) void loadValueAi(workspaceId);
  }, [active, workspaceId, loadValueAi]);

  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  useEffect(() => {
    if (active && attributes.length === 0) void fetchAttributes();
  }, [active, attributes.length, fetchAttributes]);

  const { activeMembers, nameOf } = useWorkspaceMembers(active ? workspaceId : null);
  const adminNames = useMemo(
    () => activeMembers.filter((m) => ADMIN_MEMBERSHIP_ROLES.has(m.role)).map((m) => m.name),
    [activeMembers],
  );

  // The reviewer rule per agent, for this viewer; the database enforces the same rule on accept.
  const [reviewRights, setReviewRights] = useState<Record<string, boolean>>({});
  const agentTypesKey = agents.map((a) => a.def.agentType).join(',');
  useEffect(() => {
    if (!active || !workspaceId || !userId) return;
    let live = true;
    const types = agentTypesKey ? agentTypesKey.split(',') : [];
    void Promise.all(types.map(async (t) => [t, await agentReviewerAllowed(workspaceId, t, userId)] as const)).then(
      (pairs) => live && setReviewRights(Object.fromEntries(pairs)),
    );
    return () => {
      live = false;
    };
  }, [active, workspaceId, userId, agentTypesKey]);

  const plan: BulkAskPlan = useMemo(
    () =>
      planBulkAsk(agents, {
        count: itemIds.length,
        entitled: isWorkspaceEntitledToAi(workspaceId),
        ai: { drawable: aiDrawable, needsKey: aiNeedsKey },
        isAdmin,
        adminNames,
        mayAcceptValues,
        reviewerAllowed: (t) => reviewRights[t],
        valueSettings,
        valueLabel: (key) => attributes.find((a) => a.key === key)?.label ?? key.replace(/[_-]+/g, ' '),
        personName: (id) => (id ? (activeMembers.some((m) => m.userId === id) ? nameOf(id) : null) : null),
      }),
    [agents, itemIds.length, workspaceId, aiDrawable, aiNeedsKey, isAdmin, adminNames, mayAcceptValues, reviewRights, valueSettings, attributes, activeMembers, nameOf],
  );

  // A run started here: the run store holds its progress, as it does for "Run now".
  const startRun = useAgentRunStore((s) => s.startRun);
  const [runningType, setRunningType] = useState<string | null>(null);
  const progress = useAgentRunStore((s) => (runningType ? s.byAgent[runningType]?.progress : undefined));
  const runningEntry = plan.entries.find((e) => e.agentType === runningType) ?? null;
  const running: BulkAskRunning | null = runningType
    ? {
        agentType: runningType,
        name: runningEntry?.name ?? 'The agent',
        done: progress?.done ?? 0,
        total: progress?.total ?? 0,
        unit: runningEntry?.work === 'estimates' ? 'estimates' : 'items',
      }
    : null;

  const ask = useCallback(
    async (agentType: string): Promise<BulkAskOutcome | null> => {
      const entry = plan.entries.find((e) => e.agentType === agentType);
      if (!entry || !workspaceId || runningType) return null;
      const ids = [...itemIds];
      setRunningType(agentType);
      try {
        await startRun({
          agentType,
          workspaceId,
          triggeredBy: 'manual',
          scope: { type: 'items', itemIds: ids },
          runByUserId: userId ?? null,
          batchRunner: (ws, scope, onProgress, signal) => templateBatchRunnerFor(entry.def)(ws, scope, onProgress, signal, userId ?? null),
        });
      } finally {
        setRunningType(null);
      }
      const slice = useAgentRunStore.getState().byAgent[agentType];
      return {
        name: entry.name,
        status: slice?.status ?? 'failed',
        stats: slice?.lastRunLog?.stats ?? null,
        error: slice?.error ?? null,
        run: { chosen: ids.length, cap: entry.cap, work: entry.work },
      };
    },
    [plan.entries, workspaceId, runningType, itemIds, startRun, userId],
  );

  return { plan, running, ask };
}
